import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { UserSettings } from '../Types';
import { embeddingModelService } from '../Services/embeddingModelService';
import { setListReloadType } from '../Store/features/environment/environmentSlice';
import { PaginationListType } from '../Types/PaginationParams';
import useModal from '../components/Modal/useModal';
import { EmbeddingModel } from '../Types';
import { getBreakpoint } from '../utils/breakpoint';

const useEmbeddingModels = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const dispatch = useAppDispatch();
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const modal = useModal();

    const deleteModelfunction = useCallback(
        (identifier: string) => {
            if (!userSettings.isLoggedIn) return;
            embeddingModelService.deleteModel(identifier).then(() => {
                dispatch(setListReloadType(PaginationListType.EmbeddingModels));
            });
        },
        [userSettings.isLoggedIn, dispatch],
    );

    const handleOpenConfirmDialog = useCallback(
        (apiKeyIdentifier: string) => {
            modal.deleteConfirm({
                title: i18n.translate({ id: 'modelDelete.title', sourceName: 'Models' }),
                content: i18n.translate({ id: 'modelDelete.text', sourceName: 'Models' }),
                onConfirm: () => deleteModelfunction(apiKeyIdentifier),
            });
        },
        [modal, deleteModelfunction, i18n],
    );

    const handleCopyModel = useCallback(
        async (model: EmbeddingModel) => {
            const response = await embeddingModelService.getModel(model.identifier);
            const fullModelData = response.data;

            const modelCopy = {
                ...fullModelData,
                title: `${fullModelData.title} ${i18n.translate({
                    id: 'duplicate.copyTag',
                    sourceName: 'Main',
                })}`,
                identifier: undefined,
            };

            navigate('/admin/embeddingmodels/new', { state: { duplicateData: modelCopy } });
        },
        [navigate, i18n],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        ModelsActions: {
            handleOpenConfirmDialog,
        },
        handleCopyModel,
        handleNavigate,
        i18n,
        userSettings,
        windowWidth,
        getBreakpoint,
    };
};

export default useEmbeddingModels;
